/**
 * Operations → Derived manual inputs.
 *
 * One row per derived leg a trader is allowed to fill by hand — legs whose YAML spec sets
 * ``accept_model_applied``. These are markets we deliberately will not sell (a QB rushing line
 * under ``min_offered_line``) or that books never post at all (a QB's anytime TD, a receptions
 * line for a committee back), and which a derived target still needs in order to price.
 *
 * The opt-in is per rule-LEG, not per market, so which rows appear depends on the player's
 * position: a QB's rushing and anytime-TD legs, and a RB's receptions leg. The same market on a
 * player the board does price — an RB's rushing line, a WR's receptions — is not fillable.
 *
 * Nothing here relaxes a gate. A leg blocks until a human types a number, and the number typed is
 * what the model then stands on — which is why the book columns sit right next to the input: the
 * trader is being asked to validate against what the market is actually showing.
 */
import React, { useMemo, useState } from 'react';
import {
  useDerivedManualInputs,
  useIngestModelMarketObservations,
} from '../api/hooks';
import { useAuth } from '../auth/AuthContext';
import './DerivedManualInputsPage.css';

/**
 * ``action`` is shown on the row, not just on hover. A trader landing on a Stale row has to know
 * that their earlier value no longer counts and the market stays shut until they replace it —
 * that is not something to make them infer from a drift figure.
 */
const STATUS_META = {
  stale: {
    label: 'Stale',
    cls: 'dmi-pill dmi-pill--stale',
    help: 'Books moved this past its drift limit, so the value entered earlier no longer counts.',
    action: 'Enter a new value to reopen this market. The earlier value no longer applies.',
  },
  awaiting: {
    label: 'Awaiting',
    cls: 'dmi-pill dmi-pill--awaiting',
    help: 'Nothing entered yet. The derived market is closed waiting on a value.',
    action: 'Enter a value to open this market.',
  },
  manual: {
    label: 'Manual',
    cls: 'dmi-pill dmi-pill--manual',
    help: 'Pricing off a trader-entered value, inside its drift limit.',
    action: null,
  },
  book: {
    label: 'Book',
    cls: 'dmi-pill dmi-pill--book',
    help: 'Books came back with a sellable line. Nothing to enter.',
    action: null,
  },
};

function fmtNum(v, digits = 1) {
  if (v == null || !Number.isFinite(Number(v))) return '—';
  return Number(v).toFixed(digits);
}

function fmtPct(v) {
  if (v == null || !Number.isFinite(Number(v))) return '—';
  return `${(Number(v) * 100).toFixed(1)}%`;
}

/**
 * "Do other books have this?" in one cell.
 *
 * A leg with no books anywhere and a leg suppressed under our own floor look identical from the
 * block reason alone, and they call for completely different numbers from the trader — so they
 * are spelled out differently here.
 */
function BooksCell({ row }) {
  const n = Number(row.books_n || 0);
  if (!n) return <span className="dmi-muted">no books</span>;
  return (
    <span>
      <strong>{n}</strong> book{n === 1 ? '' : 's'} @ {fmtNum(row.books_line)}
      {row.books_p_fair != null && <span className="dmi-muted"> · {fmtPct(row.books_p_fair)}</span>}
    </span>
  );
}

function EntryCell({ row, userEmail, onDone }) {
  const pinned = row.manual_entry_line;
  const seed = pinned != null ? pinned : row.books_line;
  const [line, setLine] = useState(seed != null ? String(seed) : '');
  const [price, setPrice] = useState('');
  const [err, setErr] = useState(null);
  const ingest = useIngestModelMarketObservations();

  const lineNum = Number(line);
  const priceNum = Number(price);
  const valid =
    line !== '' && Number.isFinite(lineNum) && Number.isFinite(priceNum) && priceNum > 0 && priceNum < 1;

  const submit = async () => {
    setErr(null);
    // sport + league route the write to the writer that owns this fixture. Sending them as null
    // is rejected by the request body, which reads to a trader as "Set did nothing" — so say
    // which one is missing rather than firing a request that cannot succeed.
    if (!row.sport || !row.league) {
      setErr(`Cannot route this write: snapshot is missing ${!row.sport ? 'sport' : 'league'}`);
      return;
    }
    try {
      await ingest.mutateAsync({
        fixture_id: row.fixture_id,
        market_id: row.leg_market_id,
        sport: row.sport,
        league: row.league,
        model_name: `manual:${userEmail || 'dashboard'}`,
        // Hard-replace: the trader is stating the value, not nudging the existing one.
        apply_mode: 'reseed',
        // Keep the market's real books live, so a sellable line can take this leg back.
        link_competitors: true,
        rows: [{ player_id: row.player_id, line: lineNum, p_fair: priceNum }],
      });
      setPrice('');
      if (onDone) onDone();
    } catch (e) {
      setErr(String(e?.message || e));
    }
  };

  return (
    <div className="dmi-entry">
      <input
        type="number"
        step="0.5"
        value={line}
        disabled={pinned != null}
        onChange={(e) => setLine(e.target.value)}
        title={pinned != null ? `Line is fixed at ${pinned} for this market` : 'Line'}
        placeholder="line"
        className="dmi-input dmi-input--line"
      />
      <input
        type="number"
        step="0.01"
        min="0"
        max="1"
        value={price}
        onChange={(e) => setPrice(e.target.value)}
        title="Fair probability of the over (0–1)"
        placeholder="p_over"
        className="dmi-input dmi-input--price"
      />
      <button type="button" className="dmi-set" disabled={!valid || ingest.isPending} onClick={submit}>
        {ingest.isPending ? '…' : 'Set'}
      </button>
      {err && <span className="dmi-err">{err}</span>}
    </div>
  );
}

export default function DerivedManualInputsPage() {
  const { user } = useAuth();
  const [onlyNeedsAttention, setOnlyNeedsAttention] = useState(true);
  const [sport, setSport] = useState('');
  const params = useMemo(
    () => ({ include_filled: !onlyNeedsAttention, ...(sport ? { q: sport } : {}) }),
    [onlyNeedsAttention, sport]
  );
  const { data, isLoading, error, refetch } = useDerivedManualInputs(params);

  const rows = Array.isArray(data?.rows) ? data.rows : [];

  return (
    <div className="dmi-page">
      <header className="dmi-header">
        <div>
          <h2>Derived manual inputs</h2>
          <p className="dmi-sub">
            Legs a derived market needs that we will not sell, or that books do not post. Each one
            blocks its market until someone enters a value — nothing here is auto-filled. Books
            then refine what you entered; if they move it too far it goes <strong>Stale</strong>,
            the market closes again, and it needs a new value.
          </p>
        </div>
        <div className="dmi-controls">
          <label className="dmi-check">
            <input
              type="checkbox"
              checked={onlyNeedsAttention}
              onChange={(e) => setOnlyNeedsAttention(e.target.checked)}
            />
            Needs attention only
          </label>
          <input
            type="text"
            value={sport}
            onChange={(e) => setSport(e.target.value)}
            placeholder="search player"
            className="dmi-input"
          />
          <button type="button" className="dmi-set" onClick={() => refetch()}>
            Refresh
          </button>
        </div>
      </header>

      {data?.needs_attention > 0 && (
        <div className="dmi-banner">
          <strong>{data.needs_attention}</strong> leg{data.needs_attention === 1 ? '' : 's'} closing a
          market right now.
        </div>
      )}

      {isLoading && <div className="dmi-empty">Loading…</div>}
      {error && <div className="dmi-empty dmi-empty--err">{String(error.message || error)}</div>}
      {!isLoading && !error && !rows.length && (
        <div className="dmi-empty">
          Nothing to enter. Every leg that accepts a manual value is either filled or priced by books.
        </div>
      )}

      {!!rows.length && (
        <div className="scrollable-table-container">
          <table className="ob-table ob-table-compact dmi-table">
            <thead>
              <tr>
                <th>status</th>
                <th>player</th>
                <th>leg</th>
                <th>other books</th>
                <th>why it needs you</th>
                <th>enter value</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const meta = STATUS_META[r.status] || STATUS_META.awaiting;
                return (
                  <tr key={`${r.leg_market_key}`} className={`dmi-row dmi-row--${r.status}`}>
                    <td>
                      <span
                        className={meta.cls}
                        title={
                          r.manual_value_by
                            ? `Value entered by ${r.manual_value_by}` +
                              (r.books_back ? ' — books have since taken this leg over' : '')
                            : meta.help
                        }
                      >
                        {meta.label}
                      </span>
                      {r.manual_value_by && (
                        <div className="dmi-muted dmi-small">
                          {r.books_back ? 'books back' : r.manual_value_by}
                        </div>
                      )}
                    </td>
                    <td>
                      <div className="dmi-player">
                        {r.position && (
                          <span
                            className={`dmi-pos dmi-pos--${r.position.toLowerCase()}`}
                            title={`${r.position} — which legs are fillable depends on position`}
                          >
                            {r.position}
                          </span>
                        )}
                        {r.player_id || '—'}
                      </div>
                      <div className="dmi-muted dmi-small">{r.league || r.sport || ''}</div>
                    </td>
                    <td>
                      <div>{r.leg_market_id}</div>
                      <div className="dmi-muted dmi-small">{r.target_market_id}</div>
                    </td>
                    <td>
                      <BooksCell row={r} />
                    </td>
                    <td className="dmi-reason">
                      <div>{r.block_reason || r.gate_reason || '—'}</div>
                      {meta.action && <div className="dmi-action">{meta.action}</div>}
                    </td>
                    <td>
                      {r.status === 'book' ? (
                        <span className="dmi-muted">—</span>
                      ) : (
                        <EntryCell row={r} userEmail={user?.email} onDone={refetch} />
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
